import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryForm from "@/components/EnquiryForm";
import {
    ArrowRight, GraduationCap, ChalkboardTeacher, Users, Clock, MapPin,
    Phone, EnvelopeSimple, WhatsappLogo, CheckCircle, BookOpen, Translate,
} from "@phosphor-icons/react";

const WA_MSG = encodeURIComponent(
    "Hi Wibpro Academy 👋 — I'd like to enquire about tuitions for:"
);
const WA_LINK = `https://wa.me/919100560075?text=${WA_MSG}`;

const PROGRAMS = [
    {
        id: "as-a-level", tag: "Cambridge International",
        name: "AS & A Level", classes: "AS · A2",
        body: "Full syllabus coverage for AS and A Level — Pure Maths, Mechanics, Statistics, Physics, Chemistry, Biology, Economics, Business, English Language and Literature.",
        tone: "bg-[#0A2540]",
    },
    {
        id: "igcse", tag: "Cambridge International",
        name: "IGCSE", classes: "Grade 9 · 10",
        body: "IGCSE tuition across all major boards (Cambridge / Edexcel). Extended and Core papers, past-paper drills, structured revision timelines to exam day.",
        tone: "bg-[#7C2D12]",
    },
    {
        id: "cbse", tag: "Central Board of Secondary Education",
        name: "CBSE", classes: "Class 10 · 11 · 12",
        body: "NCERT-based teaching with additional problem sets for JEE / NEET aspirants. Board exam strategy, sample papers, and previous year question analysis included.",
        tone: "bg-[#065F46]",
    },
    {
        id: "ssc", tag: "State Board (Telangana / Andhra Pradesh)",
        name: "SSC & Intermediate", classes: "Class 10 · 11 · 12",
        body: "Telugu-medium and English-medium support. SSC 10th, Intermediate MPC / BiPC — aligned with EAPCET, NEET and other state entrance goals.",
        tone: "bg-[#831843]",
    },
];

const SUBJECTS = [
    { group: "Sciences",    items: ["Physics", "Chemistry", "Biology", "Environmental Science"] },
    { group: "Mathematics", items: ["Pure Mathematics", "Mechanics", "Statistics", "Further Maths", "Business Maths"] },
    { group: "Commerce",    items: ["Accountancy", "Business Studies", "Economics", "Entrepreneurship"] },
    { group: "Humanities",  items: ["History", "Geography", "Political Science", "Sociology", "Psychology"] },
    { group: "Computing",   items: ["Computer Science", "Information Technology", "AI & Coding Fundamentals"] },
];

const LANGUAGES = [
    { name: "English",  role: "First language · CIE / IB / IELTS ready" },
    { name: "Arabic",   role: "Foreign language · Beginner to Advanced" },
    { name: "French",   role: "DELF / DALF · CIE · IB" },
    { name: "German",   role: "Goethe A1–B2 preparation" },
    { name: "Hindi",    role: "CBSE second language · daily fluency" },
    { name: "Telugu",   role: "State board · conversational · script" },
];

const FORMATS = [
    { icon: ChalkboardTeacher, title: "In-person",   body: "At our Mehdipatnam campus — small batches (6-12 students) or 1-on-1." },
    { icon: Users,             title: "Online",      body: "Live Zoom classes with recorded playback for the same batch — no compromise on attention." },
    { icon: Clock,             title: "Flexible timing", body: "Morning, evening and weekend batches. Board students get exam-crunch schedules." },
    { icon: BookOpen,          title: "Custom modules", body: "Concept revision, sample-paper marathons, or focused doubt-clearing sessions." },
];

export default function TuitionsPage() {
    return (
        <div className="min-h-screen paper-grain" data-testid="tuitions-page">
            <Navbar />

            {/* Hero */}
            <section className="mx-auto max-w-7xl px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-end">
                <div className="lg:col-span-7 animate-fade-up">
                    <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-ink/60 mb-5">
                        <span className="w-8 h-px bg-gold"></span>
                        <span>Board Tuitions · Grade 9 to 12</span>
                    </div>
                    <h1 className="font-serif font-black text-5xl md:text-7xl lg:text-[5.25rem] leading-[0.95] text-ink">
                        Every board.
                        <br />
                        <span className="italic font-light text-ink/80">Every subject. Every language.</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-ink/70 max-w-xl leading-relaxed">
                        AS & A Level, IGCSE, CBSE and SSC/Intermediate tuitions — taught by teachers who
                        remember what it was like to sit the exam themselves.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a href={WA_LINK} target="_blank" rel="noopener noreferrer">
                            <Button size="lg" className="bg-[#25D366] text-white hover:bg-[#128C7E] rounded-sm px-6 h-12" data-testid="btn-tuitions-whatsapp">
                                <WhatsappLogo size={20} weight="fill" className="mr-2"/> Enquire on WhatsApp
                            </Button>
                        </a>
                        <a href="tel:+919100560075" className="text-sm underline underline-offset-4 decoration-gold decoration-2 text-ink hover:text-gold" data-testid="link-tuitions-call">
                            or call +91 91005 60075 →
                        </a>
                    </div>
                </div>
                <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
                    <div className="relative aspect-[4/5] overflow-hidden rounded-sm border-2 border-ink/10 shadow-2xl">
                        <img src="https://images.unsplash.com/photo-1509062522246-3755977927d7?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Classroom" className="w-full h-full object-cover"/>
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent"></div>
                        <div className="absolute bottom-6 left-6 right-6 text-bone">
                            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Small batches</div>
                            <div className="font-serif text-2xl leading-tight">Every student in the room gets a turn.</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Programs */}
            <section className="border-y border-ink/10 bg-parchment/40">
                <div className="mx-auto max-w-7xl px-6 py-16">
                    <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Programs</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight mb-10">Four programs. One roof.</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {PROGRAMS.map(p => (
                            <div key={p.id} className={`p-8 rounded-sm text-bone ${p.tone}`} data-testid={`program-${p.id}`}>
                                <div className="text-[10px] uppercase tracking-widest text-gold mb-2">{p.tag}</div>
                                <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                                    <h3 className="font-serif text-3xl">{p.name}</h3>
                                    <span className="text-xs uppercase tracking-widest text-bone/60">{p.classes}</span>
                                </div>
                                <p className="text-bone/85 leading-relaxed">{p.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Subjects */}
            <section className="mx-auto max-w-7xl px-6 py-20">
                <div className="grid md:grid-cols-12 gap-8 mb-10">
                    <div className="md:col-span-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Subjects</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight gold-underline inline">
                            We teach <em className="font-light">every subject.</em>
                        </h2>
                    </div>
                    <p className="md:col-span-7 text-ink/70 text-lg leading-relaxed md:mt-6">
                        Sciences, Math, Commerce, Humanities, Computing — pick your board, pick your class,
                        and we assemble a teacher who has actually taught that exact syllabus before.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SUBJECTS.map(g => (
                        <div key={g.group} className="p-6 border border-ink/15 bg-bone rounded-sm" data-testid={`subject-group-${g.group.toLowerCase()}`}>
                            <div className="font-serif text-xl text-ink mb-4">{g.group}</div>
                            <ul className="space-y-2">
                                {g.items.map(item => (
                                    <li key={item} className="flex items-center gap-2 text-sm text-ink/80">
                                        <CheckCircle size={14} weight="fill" className="text-gold shrink-0"/> {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            {/* Languages */}
            <section className="bg-ink text-bone py-20">
                <div className="mx-auto max-w-7xl px-6">
                    <div className="flex items-baseline justify-between flex-wrap gap-3 mb-10">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Languages</div>
                            <h2 className="font-serif text-4xl md:text-5xl">Six languages, taught properly.</h2>
                        </div>
                        <Translate size={40} weight="duotone" className="text-gold"/>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {LANGUAGES.map(l => (
                            <div key={l.name} className="p-5 border border-bone/15 rounded-sm hover:border-gold/50 transition-colors" data-testid={`language-${l.name.toLowerCase()}`}>
                                <div className="font-serif text-2xl">{l.name}</div>
                                <div className="text-xs text-bone/60 mt-1">{l.role}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How we teach */}
            <section className="mx-auto max-w-7xl px-6 py-20">
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">How we teach</div>
                <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight mb-10">On your terms.</h2>
                <div className="grid md:grid-cols-4 gap-6">
                    {FORMATS.map(f => (
                        <div key={f.title} className="p-6 border border-ink/10 bg-bone rounded-sm hover:border-ink/40 transition-colors" data-testid={`format-${f.title.toLowerCase().replace(/\s/g,'-')}`}>
                            <f.icon size={28} weight="duotone" className="text-ink mb-4"/>
                            <div className="font-serif text-xl text-ink mb-2">{f.title}</div>
                            <div className="text-sm text-ink/70 leading-relaxed">{f.body}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Contact */}
            <section id="contact" className="border-t border-ink/10 bg-parchment/40">
                <div className="mx-auto max-w-7xl px-6 py-20 grid lg:grid-cols-2 gap-12">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Enquire</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight">Book a demo class.</h2>
                        <p className="mt-4 text-ink/70">
                            Tell us your board, class and subject. We'll set up a 30-minute demo with the
                            right teacher — free, no strings attached.
                        </p>
                        <div className="mt-8 space-y-4">
                            <div className="flex items-start gap-3" data-testid="tuitions-contact-address">
                                <MapPin size={20} weight="fill" className="text-gold mt-1"/>
                                <div>
                                    <div className="font-serif text-lg text-ink">Wibpro Academy · Head Office</div>
                                    <div className="text-sm text-ink/70">2nd floor, Safa Arcade, Opposite pillar 15,<br/>Mehdipatnam, Hyderabad, Telangana, India</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3" data-testid="tuitions-contact-phone">
                                <Phone size={20} weight="fill" className="text-gold mt-1"/>
                                <a href="tel:+919100560075" className="font-serif text-lg text-ink hover:text-gold">+91 91005 60075</a>
                            </div>
                            <div className="flex items-start gap-3">
                                <EnvelopeSimple size={20} weight="fill" className="text-gold mt-1"/>
                                <a href="mailto:hello@wibpro.com" className="font-serif text-lg text-ink hover:text-gold">hello@wibpro.com</a>
                            </div>
                        </div>
                    </div>
                    <div className="border border-ink/15 rounded-sm bg-bone p-8">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-3">
                            <GraduationCap size={14} weight="duotone"/> Signature
                        </div>
                        <h3 className="font-serif text-3xl text-ink leading-tight">Mathematics with Mr. Calculus.</h3>
                        <p className="mt-3 text-ink/70">
                            Our lead faculty <span className="font-serif italic text-ink">Abul Faisal</span> — known to
                            his students simply as <span className="font-serif italic text-ink">Mr. Calculus</span> —
                            personally handles the Pure Maths and Mechanics tracks. Ask for a slot in his batch.
                        </p>
                        <a href={WA_LINK} target="_blank" rel="noopener noreferrer">
                            <Button className="mt-6 w-full bg-[#25D366] text-white hover:bg-[#128C7E] rounded-sm h-11" data-testid="btn-tuitions-book">
                                <WhatsappLogo size={16} weight="fill" className="mr-2"/> Book a slot on WhatsApp <ArrowRight size={16} className="ml-2"/>
                            </Button>
                        </a>
                    </div>
                </div>

                {/* Enquiry form */}
                <div className="mx-auto max-w-7xl px-6 pb-20">
                    <div className="grid lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-5">
                            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Or drop a message</div>
                            <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                                Prefer we call you?
                            </h3>
                            <p className="mt-4 text-ink/70">
                                Fill in the form and we'll ring you at a time that works — usually within
                                one working day. No pressure, no sales pitch.
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            <EnquiryForm
                                source="tuitions"
                                interestPlaceholder="e.g. IGCSE Grade 10 Maths · CBSE Class 12 Physics"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
